import React, { useState ,useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope } from 'react-icons/fa';
import { baseURL } from '../../../../network/baseUrl';
import { useTranslation } from 'react-i18next';
import { setLanguages } from '../../../../redux/slices/languageSlice';
import { useDispatch } from 'react-redux';

const ResetPassword = () => {
  const { t , i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false); // State for loading spinner
  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  // Added: step state and OTP value
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [otp, setOtp] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.(com|io|co\.in)$/;
    return emailRegex.test(email);
  };

  useEffect(() => {

    const currentLang = localStorage.getItem("selectedLanguage") || "en";

    console.log("Detected i18n.language:", currentLang);
    console.log("t(auth.loginTitle):", i18n.t("auth.loginTitle"));


    dispatch(setLanguages(currentLang));



    const disableBackForward = () => {
      window.history.pushState(null, '', window.location.href);
      window.onpopstate = () => {
        window.history.pushState(null, '', window.location.href);
      };
    };
    disableBackForward();
    i18n.changeLanguage(currentLang).then(() => setReady(true));

    return () => {
      window.onpopstate = null;
    };
  }, []);

  const handleResetPasswordRequest = async (e) => {
    e.preventDefault();

    // Validate email
    if (!validateEmail(email)) {
      setErrorMessage(
        t("loginToasters.invalidEmail")
      );
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true); // Show loader during API call


    try {
      const response = await fetch(`${baseURL}/auth/reset-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('OTP sent:', data);
        // Update message to reflect OTP sent and move to OTP step
        setSuccessMessage(t("requestResetPassword.otpSent") || "OTP has been sent to your email.");
        setErrorMessage('');
        setStep('otp');
      } else {
        const error = await response.json();
        console.error(error);
        setErrorMessage(t("loginToasters.mailNotRegistered"));
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(t("loginToasters.genericError"));
    } finally {
      setLoading(false); // Hide loader after API call
    }
  };

  // New: handle OTP verification to trigger email with reset link
  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp || otp.trim().length === 0) {
      setErrorMessage(t("requestResetPassword.otpRequired") || "OTP is required");
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${baseURL}/auth/reset-password/verify-otp/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Reset link sent:', data);
        setSuccessMessage(t("loginToasters.passwordSentToMail") || "Password reset link has been sent to your email.");
        setErrorMessage('');
        // Optionally clear OTP
        // setOtp('');
      } else {
        const error = await response.json();
        console.error(error);
        if (error?.error?.toLowerCase().includes('expired')) {
          setErrorMessage(t("requestResetPassword.otpExpired") || "OTP has expired. Please request again.");
        } else {
          setErrorMessage(t("requestResetPassword.otpInvalid") || "Invalid OTP. Please try again.");
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(t("loginToasters.genericError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900 p-4">
      <div className="relative bg-gray-800 shadow-lg rounded-lg p-6 w-full max-w-md">
        <h2 className="text-center mb-6 font-bold text-2xl text-white">
          {step === 'email' ? t('requestResetPassword.title') : (t('requestResetPassword.enterOtpTitle') || 'Enter OTP')}
        </h2>

        <button
          className="absolute top-3 right-3 text-white hover:text-gray-300 text-2xl"
          onClick={() => window.location.href = '/'}
        >
          &times;
        </button>

        {/* Step 1: Ask for email */}
        {step === 'email' && (
          <form onSubmit={handleResetPasswordRequest}>
            <div className="mb-4">
              <input
                type="email"
                className="w-full bg-gray-900 text-gray-300 border-0 rounded-md p-4 text-center font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                id="email"
                placeholder={t('requestResetPassword.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {errorMessage && (
              <div className="bg-red-900 text-red-200 p-3 rounded-md text-center mb-4" role="alert">
                {errorMessage}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-900 text-green-200 p-3 rounded-md text-center mb-4" role="alert">
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-4 rounded-md transition duration-200 disabled:opacity-70"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('requestResetPassword.sending')}</span>
                </div>
              ) : (
                t('requestResetPassword.submitButton')
              )}
            </button>

            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/login')}
                className="text-blue-400 hover:text-blue-300 font-medium"
                type="button"
              >
                {t('requestResetPassword.backToLogin')}
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Ask for OTP */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            <div className="mb-2 text-gray-300 text-sm">
              {t('requestResetPassword.otpInstruction') || 'Enter the 4-digit code we sent to your email.'}
            </div>
            <div className="mb-4">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="w-full bg-gray-900 text-gray-300 border-0 rounded-md p-4 text-center font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                id="otp"
                placeholder={t('requestResetPassword.otpPlaceholder') || 'Enter OTP'}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />
            </div>

            {errorMessage && (
              <div className="bg-red-900 text-red-200 p-3 rounded-md text-center mb-4" role="alert">
                {errorMessage}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-900 text-green-200 p-3 rounded-md text-center mb-4" role="alert">
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-4 rounded-md transition duration-200 disabled:opacity-70"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('requestResetPassword.verifying') || 'Verifying...'}</span>
                </div>
              ) : (
                t('requestResetPassword.verifyButton') || 'Verify OTP'
              )}
            </button>

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setStep('email')}
                className="text-blue-400 hover:text-blue-300 font-medium"
                type="button"
              >
                {t('requestResetPassword.changeEmail') || 'Change email'}
              </button>
              <button
                onClick={() => handleResetPasswordRequest(new Event('submit'))}
                className="text-blue-400 hover:text-blue-300 font-medium"
                type="button"
                disabled={loading}
                title={t('requestResetPassword.resendTooltip') || 'Resend OTP to your email'}
              >
                {t('requestResetPassword.resendOtp') || 'Resend OTP'}
              </button>
            </div>

            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/login')}
                className="text-blue-400 hover:text-blue-300 font-medium"
                type="button"
              >
                {t('requestResetPassword.backToLogin')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;